import {
  clearSilentReauth,
  markSilentReauthTried,
  silentReauthAlreadyTried
} from "@appretio/auth";
import { ReauthInProgressError } from "../errorHandling/reauthInProgress";

type AccessTokenGetter = () => Promise<string>;

interface ReauthRedirectOptions {
  authorizationParams?: { prompt: "none" };
  appState: { returnTo: string };
}

type LoginRedirect = (options: ReauthRedirectOptions) => Promise<void>;

/**
 * Wraps a token getter so that an acquisition failure (e.g. the Auth0 refresh
 * token has expired) triggers re-authentication. The first failure attempts a
 * silent `prompt=none` redirect: if the upstream SSO session is still valid the
 * user returns with a fresh token. If silent renewal already failed once this
 * session (e.g. the SSO session is gone / cookies blocked), escalate straight
 * to interactive login — otherwise `prompt=none` keeps bouncing back with
 * `login_required` and the page loops indefinitely.
 */
export function withReauthFallback(
  getToken: AccessTokenGetter,
  loginWithRedirect: LoginRedirect
): AccessTokenGetter {
  return async function getTokenWithReauth() {
    try {
      const token = await getToken();
      clearSilentReauth(); // a working token proves the session recovered
      return token;
    } catch (error) {
      const returnTo = window.location.pathname + window.location.search;
      if (silentReauthAlreadyTried()) {
        clearSilentReauth();
        await loginWithRedirect({ appState: { returnTo } });
      } else {
        markSilentReauthTried();
        await loginWithRedirect({
          authorizationParams: { prompt: "none" },
          appState: { returnTo }
        });
      }
      // Distinct error type so the query error handler can suppress the toast —
      // we're redirecting, not failing.
      throw new ReauthInProgressError({ cause: error });
    }
  };
}
